import { router } from "@inertiajs/react"
import { Calendar, Plug, Plus, UserPlus, X } from "lucide-react"
import { useEffect, useState } from "react"

import calComIcon from "@/assets/cal_com.svg"
import calendlyIcon from "@/assets/calendly.svg"
import { CapabilityCard } from "@/components/agents/setup/capability-card"
import {
  PRESET_COLORS,
  PositionOption,
} from "@/components/agents/setup/chatbot/appearance-step"
import type { CalendarEventType } from "@/components/agents/setup/chatbot/conversions-step"
import {
  InstallSnippet,
  normalizeDomain,
} from "@/components/agents/setup/chatbot/install-snippet"
import {
  type BubblePosition,
  type ChatbotSetupData,
  deriveBooking,
  deriveCollect,
} from "@/components/agents/setup/chatbot/types"
import {
  CUSTOM_INSTRUCTIONS_PLACEHOLDERS,
  CustomInstructionsField,
} from "@/components/agents/setup/custom-instructions"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Switch } from "@/components/ui/switch"
import {
  Tabs,
  TabsBar,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { agentPath, settingsIntegrationsPath } from "@/routes"

// The slice of setup_data the Edit drawer owns — the same config the agent was
// created with (the install snippet isn't editable config, so it's omitted).
type EditableFields = ChatbotSetupData

const DEFAULT_FIELDS: EditableFields = {
  bot_name: "",
  primary_color: "#2563EB",
  bubble_position: "bottom-right",
  welcome_message: "Hi there! How can I help you today?",
  popup_enabled: true,
  conversation_starters: [
    "What services do you offer?",
    "What are your hours?",
    "How can I reach you?",
  ],
  collect_enabled: true,
  booking_enabled: false,
  appointment_calendar_kind: "",
  appointment_event_type_uri: "",
  embed_domain: "",
  custom_instructions: "",
}

// IMP-463: agents saved before the capability split carry only the legacy
// goal/escalation keys — derive the two toggles from them (same rules as the
// wizard and the server).
export function initFields(initial?: Partial<EditableFields>): EditableFields {
  const merged = { ...DEFAULT_FIELDS, ...initial }
  return {
    ...merged,
    collect_enabled: initial ? deriveCollect(initial) : merged.collect_enabled,
    booking_enabled: initial ? deriveBooking(initial) : merged.booking_enabled,
  }
}

export function ChatbotEditSheet({
  open,
  onOpenChange,
  agentUid,
  agentName,
  initial,
  connectedCalendars,
  calendlyEventTypes,
  calComEventTypes,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  agentUid: string
  agentName: string
  initial?: Partial<EditableFields>
  connectedCalendars: Record<string, boolean>
  calendlyEventTypes: CalendarEventType[]
  calComEventTypes: CalendarEventType[]
}) {
  const [fields, setFields] = useState<EditableFields>(() =>
    initFields(initial),
  )
  const [processing, setProcessing] = useState(false)

  // Reset to the server's view each time the drawer opens so a prior cancel
  // doesn't leave stale edits behind.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (open) setFields(initFields(initial))
  }, [open, initial])

  function update<K extends keyof EditableFields>(
    key: K,
    value: EditableFields[K],
  ) {
    setFields((prev) => ({ ...prev, [key]: value }))
  }

  // Persist the edited config. The widget loads its config at runtime, so the
  // saved changes take effect on the live widget without re-installing.
  function save() {
    setProcessing(true)
    router.patch(
      agentPath({ id: agentUid }),
      {
        setup_data: fields as unknown as Record<
          string,
          string | number | boolean | string[]
        >,
      },
      {
        preserveScroll: true,
        onFinish: () => setProcessing(false),
        onSuccess: () => onOpenChange(false),
      },
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="px-6 py-4">
          <SheetTitle>Edit {agentName}</SheetTitle>
        </SheetHeader>

        <Tabs
          defaultValue="behavior"
          className="flex min-h-0 flex-1 flex-col gap-0"
        >
          <TabsBar>
            <TabsList variant="line">
              <TabsTrigger value="behavior">Behavior</TabsTrigger>
              <TabsTrigger value="messages">Messages</TabsTrigger>
              <TabsTrigger value="appointments">Appointments</TabsTrigger>
              <TabsTrigger value="appearance">Appearance</TabsTrigger>
              <TabsTrigger value="settings">Install</TabsTrigger>
              <TabsTrigger value="customization">Customization</TabsTrigger>
            </TabsList>
          </TabsBar>

          <TabsContent
            value="appearance"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <AppearancePanel fields={fields} onChange={update} />
          </TabsContent>
          <TabsContent
            value="behavior"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <BehaviorPanel fields={fields} onChange={update} />
          </TabsContent>
          <TabsContent
            value="messages"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <MessagesPanel fields={fields} onChange={update} />
          </TabsContent>
          <TabsContent
            value="appointments"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <AppointmentsPanel
              fields={fields}
              onChange={update}
              connectedCalendars={connectedCalendars}
              calendlyEventTypes={calendlyEventTypes}
              calComEventTypes={calComEventTypes}
            />
          </TabsContent>
          <TabsContent
            value="settings"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <SettingsPanel
              fields={fields}
              onChange={update}
              agentUid={agentUid}
            />
          </TabsContent>
          <TabsContent
            value="customization"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <CustomizationPanel fields={fields} onChange={update} />
          </TabsContent>
        </Tabs>

        <SheetFooter className="border-t px-6 py-4">
          <Button
            className="w-full rounded-sm"
            onClick={save}
            disabled={processing}
          >
            {processing ? "Saving…" : "Save changes"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

// ── Shared panel props & primitives ────────────────────────────────────────

interface PanelProps {
  fields: EditableFields
  onChange: <K extends keyof EditableFields>(
    key: K,
    value: EditableFields[K],
  ) => void
}

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold">{title}</p>
        {description && (
          <p className="text-muted-foreground mt-0.5 text-sm leading-relaxed">
            {description}
          </p>
        )}
      </div>
      {children}
    </div>
  )
}

function StringListEditor({
  items,
  onChange,
  addLabel,
  maxLength,
  placeholder,
}: {
  items: string[]
  onChange: (next: string[]) => void
  addLabel: string
  maxLength?: number
  placeholder?: string
}) {
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={item}
            maxLength={maxLength}
            placeholder={placeholder}
            onChange={(e) =>
              onChange(items.map((x, j) => (j === i ? e.target.value : x)))
            }
          />
          <button
            type="button"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
            aria-label="Remove"
          >
            <X className="size-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...items, ""])}
        className="text-muted-foreground hover:text-foreground flex items-center gap-2 text-sm transition-colors"
      >
        <Plus className="size-4" /> {addLabel}
      </button>
    </div>
  )
}

// ── Appearance ─────────────────────────────────────────────────────────────

const POSITION_OPTIONS: { value: BubblePosition; label: string }[] = [
  { value: "bottom-right", label: "Bottom right" },
  { value: "bottom-left", label: "Bottom left" },
]

export function AppearancePanel({ fields, onChange }: PanelProps) {
  return (
    <>
      <Section title="Display name">
        <Input
          value={fields.bot_name}
          onChange={(e) => onChange("bot_name", e.target.value)}
          placeholder="Acme Co"
        />
        <p className="text-muted-foreground text-xs">
          The title of the chat window. Usually your business name.
        </p>
      </Section>

      <Section title="Primary color">
        <div className="flex flex-wrap items-center gap-2">
          {PRESET_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => onChange("primary_color", color)}
              className={cn(
                "size-8 rounded-full border-2 transition-transform",
                fields.primary_color === color
                  ? "border-foreground scale-110"
                  : "border-transparent",
              )}
              style={{ backgroundColor: color }}
              aria-label={color}
            />
          ))}
          <label className="relative">
            <input
              type="color"
              value={fields.primary_color}
              onChange={(e) => onChange("primary_color", e.target.value)}
              className="absolute inset-0 cursor-pointer opacity-0"
            />
            <span className="border-border text-muted-foreground hover:bg-muted/30 flex size-8 items-center justify-center rounded-full border text-xs transition-colors">
              +
            </span>
          </label>
        </div>
      </Section>

      <Section title="Bubble position">
        <div className="grid grid-cols-2 gap-2">
          {POSITION_OPTIONS.map((opt) => (
            <PositionOption
              key={opt.value}
              value={opt.value}
              label={opt.label}
              selected={fields.bubble_position === opt.value}
              onClick={() => onChange("bubble_position", opt.value)}
            />
          ))}
        </div>
      </Section>
    </>
  )
}

// ── Customization (IMP-373) ────────────────────────────────────────────────

export function CustomizationPanel({ fields, onChange }: PanelProps) {
  return (
    <CustomInstructionsField
      value={fields.custom_instructions}
      placeholder={CUSTOM_INSTRUCTIONS_PLACEHOLDERS["website-chat"]}
      onChange={(value) => onChange("custom_instructions", value)}
    />
  )
}

// ── Behavior ───────────────────────────────────────────────────────────────

export function BehaviorPanel({ fields, onChange }: PanelProps) {
  return (
    <>
      <Section title="Welcome message">
        <Textarea
          rows={3}
          value={fields.welcome_message}
          onChange={(e) => onChange("welcome_message", e.target.value)}
          placeholder="e.g. Hi there! How can I help you today?"
        />
        <div className="flex items-start justify-between gap-4 pt-1">
          <div>
            <Label htmlFor="edit-popup-enabled" className="cursor-pointer">
              Pre-open popup
            </Label>
            <p className="text-muted-foreground mt-1 text-sm">
              Show a small bubble with this message above the launcher to invite
              visitors in.
            </p>
          </div>
          <Switch
            id="edit-popup-enabled"
            checked={fields.popup_enabled}
            onCheckedChange={(v) => onChange("popup_enabled", v)}
          />
        </div>
      </Section>

      <Section
        title="Conversation starters"
        description="Suggested prompts shown in the chat window to nudge visitors."
      >
        <StringListEditor
          items={fields.conversation_starters}
          onChange={(next) => onChange("conversation_starters", next)}
          addLabel="Add starter"
          maxLength={50}
        />
      </Section>
    </>
  )
}

// ── Conversions ────────────────────────────────────────────────────────────

const CALENDAR_OPTIONS: {
  id: "cal_com" | "calendly"
  label: string
  logo: string
}[] = [
  { id: "cal_com", label: "Cal.com", logo: calComIcon },
  { id: "calendly", label: "Calendly", logo: calendlyIcon },
]

interface ConversionsPanelProps extends PanelProps {
  connectedCalendars: Record<string, boolean>
  calendlyEventTypes: CalendarEventType[]
  calComEventTypes: CalendarEventType[]
}

function AppointmentFields({
  fields,
  onChange,
  connectedCalendars,
  calendlyEventTypes,
  calComEventTypes,
}: ConversionsPanelProps) {
  const kind = fields.appointment_calendar_kind
  const eventTypes = kind === "calendly" ? calendlyEventTypes : calComEventTypes
  const showEventTypePicker = kind !== "" && connectedCalendars[kind]

  return (
    <>
      <Section title="Calendar integration">
        <div className="grid grid-cols-2 gap-3">
          {CALENDAR_OPTIONS.map(({ id, label, logo }) => {
            const isConnected = connectedCalendars[id]
            const selected = kind === id
            return (
              <div
                key={id}
                onClick={() => {
                  if (isConnected) {
                    onChange("appointment_calendar_kind", id)
                    onChange("appointment_event_type_uri", "")
                  }
                }}
                className={cn(
                  "rounded-md border p-3 transition-colors",
                  selected
                    ? "border-blue-300 bg-gradient-to-b from-blue-50 to-white dark:from-blue-950/60 dark:to-blue-900/20"
                    : "border-border",
                  isConnected && "cursor-pointer",
                  isConnected && !selected && "hover:border-blue-200",
                )}
              >
                <div className="mb-4 flex items-start justify-between">
                  <img src={logo} alt={label} className="size-7" />
                  {isConnected ? (
                    <span className="border-border inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-medium">
                      Connected
                    </span>
                  ) : (
                    <a
                      href={settingsIntegrationsPath()}
                      onClick={(e) => e.stopPropagation()}
                      className="border-border hover:bg-muted flex size-6 items-center justify-center rounded-sm border transition-colors"
                      aria-label={`Connect ${label}`}
                    >
                      <Plug className="text-muted-foreground size-3.5" />
                    </a>
                  )}
                </div>
                <p
                  className={cn(
                    "text-sm font-medium",
                    selected && "text-blue-600 dark:text-blue-400",
                  )}
                >
                  {label}
                </p>
              </div>
            )
          })}
        </div>
      </Section>

      {showEventTypePicker && (
        <Section title="Event type">
          <select
            value={fields.appointment_event_type_uri}
            onChange={(e) =>
              onChange("appointment_event_type_uri", e.target.value)
            }
            className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
          >
            <option value="">Choose an event type…</option>
            {eventTypes.map((et) => (
              <option key={et.value} value={et.value}>
                {et.label}
              </option>
            ))}
          </select>
        </Section>
      )}
    </>
  )
}

// IMP-463/469: two independent capabilities replace the exclusive 3-way goal,
// each with its own tab (matching the receptionist's tab-per-capability
// shape). Both can be on: the bot books visitors who are ready to schedule
// and captures details from the rest. Escalation is derived server-side
// (either on → collect details; both off → share the business's contact
// info), so there's no "when the bot can't help" control anymore.
export function MessagesPanel({ fields, onChange }: PanelProps) {
  return (
    <>
      <CapabilityCard
        icon={UserPlus}
        on={fields.collect_enabled}
        title="Take messages"
        description="Takes a name, a way to reach them, and the reason they got in touch."
        onToggle={(v) => onChange("collect_enabled", v)}
      />

      {/* The toggle is all there is now.

          IMP-487 retired "Contact details to request": the message minimum is a
          fixed contract on every agent, and those checkboxes could only ever
          configure a worse message.

          IMP-488 retired "Qualifying questions": what's worth learning about a
          lead is derived from what the assistant knows about the business, so
          all three agents judge the same person by the same standard. Custom
          instructions remain the owner's escape hatch for steering. */}
    </>
  )
}

export function AppointmentsPanel(props: ConversionsPanelProps) {
  const { fields, onChange } = props

  return (
    <CapabilityCard
      icon={Calendar}
      on={fields.booking_enabled}
      title="Book appointments"
      description="Offers your open times and books the slot before the conversation ends."
      onToggle={(v) => onChange("booking_enabled", v)}
    >
      <AppointmentFields {...props} />
    </CapabilityCard>
  )
}

// ── Settings ────────────────────────────────────────────────────────────────

export function SettingsPanel({
  fields,
  onChange,
  agentUid,
}: PanelProps & { agentUid: string }) {
  return (
    <>
      <Section title="Embed domain">
        <Input
          value={fields.embed_domain}
          onChange={(e) => onChange("embed_domain", e.target.value)}
          onBlur={(e) =>
            onChange("embed_domain", normalizeDomain(e.target.value))
          }
          placeholder="yoursite.com"
        />
        <p className="text-muted-foreground text-xs">
          For security, the widget only works on this domain.
        </p>
      </Section>

      <Section
        title="Install snippet"
        description="Paste this on your site to add the chat widget."
      >
        <InstallSnippet agentUid={agentUid} />
      </Section>
    </>
  )
}
